import type { ClerkProviderProps } from "@clerk/react";

/** Binds Clerk's color system to the live MorrowOS palette and pushes its
 * stock component chrome into the FNR executive look: 8px radius, hairline
 * borders, soft lift shadows, and sky-blue primary actions. CSS variables make
 * theme changes propagate to portaled sign-in and profile surfaces without
 * remounting Clerk. */
export const clerkAppearance = {
  options: {
    logoImageUrl: "/brand/morrow-mark.png",
  },
  variables: {
    colorPrimary: "var(--primary)",
    colorPrimaryForeground: "var(--primary-foreground)",
    colorDanger: "var(--error)",
    colorSuccess: "var(--success)",
    colorWarning: "var(--warning)",
    colorNeutral: "var(--foreground)",
    colorForeground: "var(--foreground)",
    // The stock dark theme's muted token is translucent. Clerk uses this as
    // the footer's background, so derive an opaque muted surface from the card.
    colorMuted: "color-mix(in srgb, var(--card) 98%, var(--foreground))",
    colorMutedForeground: "var(--muted-foreground)",
    colorBackground: "var(--card)",
    colorInputForeground: "var(--foreground)",
    colorInput: "var(--secondary)",
    colorRing: "var(--ring)",
    fontFamily: "var(--font-sans)",
    borderRadius: "8px",
  },
  elements: {
    card: {
      border: "1px solid var(--border)",
      borderRadius: "8px",
      boxShadow: "var(--fnr-modal-shadow)",
    },
    formFieldInput: {
      border: "1px solid var(--border)",
      borderRadius: "8px",
    },
    formButtonPrimary: {
      border: "1px solid var(--primary)",
      borderRadius: "8px",
      boxShadow: "none",
      fontWeight: "600",
    },
    socialButtonsBlockButton: {
      border: "1px solid var(--border)",
      borderRadius: "8px",
    },
    formFieldErrorText: { color: "var(--error-foreground)" },
    formFieldWarningText: { color: "var(--warning-foreground)" },
    formFieldSuccessText: { color: "var(--success-foreground)" },
    otpCodeFieldErrorText: { color: "var(--error-foreground)" },
    otpCodeFieldSuccessText: { color: "var(--success-foreground)" },
    // Clerk's default user face is a circle. Square it to the same 8px
    // corners as org logos, cards, and inputs.
    avatarBox: {
      borderRadius: "8px",
      overflow: "hidden",
    },
    avatarImage: {
      borderRadius: "8px",
    },
    userButtonAvatarBox: {
      borderRadius: "8px",
      overflow: "hidden",
    },
    userButtonAvatarImage: {
      borderRadius: "8px",
    },
    userPreviewAvatarBox: {
      borderRadius: "8px",
      overflow: "hidden",
    },
    userPreviewAvatarImage: {
      borderRadius: "8px",
    },
    organizationPreviewAvatarBox: {
      borderRadius: "8px",
      overflow: "hidden",
    },
    organizationPreviewAvatarImage: {
      borderRadius: "8px",
    },
    // Dim + explicitly center the modal on the viewport. A transparent
    // backdrop let the card float over the app chrome and read as "off-center
    // / broken"; a proper scrim ties it back to the center of the screen.
    modalBackdrop: {
      backgroundColor: "color-mix(in srgb, var(--background) 72%, transparent)",
      backdropFilter: "blur(2px)",
      alignItems: "center",
      justifyContent: "center",
    },
  },
} satisfies NonNullable<ClerkProviderProps["appearance"]>;
